import { Modal, Stack, Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { desktopSx } from "src/utils/styles/modalStyles";
import { FormEvent, useCallback, useState } from "react";
import { Question } from "src/api/types";
import { useCreateIntakeQuestionMutation } from "src/api/services/intakeQuestionsApi";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import QuestionForm from "src/components/QuestionForm";

interface Props {
  open: boolean;
  onClose: () => void;
  intakeId: number;
}

const AddNewIntakeQuestion = ({ onClose, open, intakeId }: Props) => {
  const [createQuestion, { isLoading }] = useCreateIntakeQuestionMutation();
  const logError = useSentryLogger("error");
  const [error, setError] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(
    null,
  );

  const [nextQuestion, setNextQuestion] = useState<Question | null>(null);

  const [position, setPosition] = useState<number>(0);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (selectedQuestion == null) {
        setError("Question is required");
        return;
      }

      setError("");

      try {
        await createQuestion({
          intakeId,
          body: {
            question_id: selectedQuestion.id,
            question_position: position,
            next_question: nextQuestion?.slug,
          },
        }).unwrap();

        setPosition(0);
        setSelectedQuestion(null);
        setNextQuestion(null);
        onClose();
      } catch (e) {
        logError("Failed to create intake question", e);
        setError("Failed to create intake question");
      }
    },
    [
      createQuestion,
      intakeId,
      logError,
      nextQuestion?.slug,
      onClose,
      position,
      selectedQuestion,
    ],
  );

  return (
    <Modal open={open}>
      <Stack sx={desktopSx} gap={2} maxWidth={400}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Question
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
        <QuestionForm
          error={error}
          isLoading={isLoading}
          nextQuestion={nextQuestion}
          onSubmit={handleSubmit}
          position={position}
          setPosition={setPosition}
          selectedQuestion={selectedQuestion}
          setError={setError}
          setNextQuestion={setNextQuestion}
          setSelectedQuestion={setSelectedQuestion}
        />
      </Stack>
    </Modal>
  );
};

export default AddNewIntakeQuestion;
